import { useState, useRef } from "react";
import { useQueryClient } from "react-query";
import { useTranslation } from 'react-i18next';
import { Modal, ModalSize } from "@components/new/Modal";
// import { Button, ButtonSize, ButtonType } from "@components/new/Button";
import Stepper, { EStepState } from "@components/new/Stepper";
import { MessageModalContent } from '@components/modals/MessageModalNew';
// import {
//   getFBPRequestDetails,
// } from "@api/apiClient/fbp";
import Form from "./Form";
import { FormHandle } from "./types";
import styles from "./CreateWithdrawalModal.module.scss";

interface OrderModalProps {
  onClose: () => void;
  sellerWarehouseId: string;
  //fbpWarehouseId: string;
}

interface ISubmitButtonProps {
  disabled: boolean;
  isLoading?: boolean;
}

enum steps {
  FORM = "form",
  SUCCESS = "success",
  ERROR = "error",
}

const CreateWithdrawalModal = ({ onClose, sellerWarehouseId }: OrderModalProps) => {
  const [step, setStep] = useState(steps.FORM);
  const [ buttonProps, setButtonProps ] = useState<ISubmitButtonProps>({
    disabled: false,
    isLoading: false,
  });
  const [ headerMessage, setHeaderMessage ] = useState<React.ReactNode>();
  const { t } = useTranslation();
  const formRef = useRef<FormHandle>({ submit: () => {} });
  const queryClient = useQueryClient();

  const handleStatusChange = (props: ISubmitButtonProps, msg: React.ReactNode) => {
    setButtonProps({
      disabled: props.disabled ?? false,
      isLoading: props.isLoading ?? false,
    });
    setHeaderMessage(msg);
  }

  const onSuccess = () => {
    queryClient.invalidateQueries(
      { queryKey: ["getFBPRequests"] },
    );
    queryClient.invalidateQueries(
      { queryKey: ["getFBPStocks"] },
    );
    queryClient.invalidateQueries(
      { queryKey: ["getFBPWithdrawalRequests"] },
    );
    setStep(steps.SUCCESS);
  }

  const onError = () => {
    setStep(steps.ERROR);
  }

  const stepsData = [
    { text: t('fbpPage.withdrawalModal.step-1'), state: step === steps.FORM ? EStepState.ENABLED : EStepState.SUCCESS },
    {
      text: t('fbpPage.withdrawalModal.step-2'),
      state: (step === steps.SUCCESS ? EStepState.SUCCESS : (
        step === steps.ERROR ? EStepState.ERROR : EStepState.DISABLED
      )),
    },
  ]

  if (step === steps.SUCCESS) {
    return (
      <Modal
        isOpen={true}
        title={t('fbpPage.withdrawalModal.title')}
        headerContent={<Stepper steps={stepsData} gap="small" />}
        size={ModalSize.L}
        onClose={onClose}
        footerButtons={[
          {
            title: t('fbpPage.withdrawalModal.closeBtn'),
            onClick: onClose,
          },
        ]}
      >
        <MessageModalContent
          className={styles.successContent}
          descriptionClassName={styles.successDescription}
          title={t('fbpPage.withdrawalModal.successTitle')}
          description={t('fbpPage.withdrawalModal.successDescription')}
          variant="success"
        />
      </Modal>
    )
  }

  if (step === steps.ERROR) {
    return (
      <Modal
        isOpen={true}
        title={t('fbpPage.withdrawalModal.title')}
        headerContent={<Stepper steps={stepsData} gap="small" />}
        size={ModalSize.L}
        onClose={onClose}
        footerButtons={[
          {
            title: t('fbpPage.withdrawalModal.closeBtn'),
            onClick: onClose,
          },
          {
            title: t('fbpPage.withdrawalModal.repeatBtn'),
            onClick: () => setStep(steps.FORM),
          },
        ]}
      >
        <MessageModalContent
          title={t('fbpPage.withdrawalModal.errorTitle')}
          description={t('fbpPage.withdrawalModal.errorDescription')}
          variant="warning"
        />
      </Modal>
    )
  }

  return (
    <Modal
      isOpen={true}
      title={t('fbpPage.withdrawalModal.title')}
      headerContent={<Stepper steps={stepsData} gap="small" />}
      headerTitleContent={headerMessage}
      size={ModalSize.L}
      onClose={onClose}
      confirmCancel
      confirmCancelDescription={t('fbpPage.withdrawalModal.closeConfirmDescription')}
      footerButtons={[
        {
          title: t('fbpPage.withdrawalModal.closeBtn'),
        },
        {
          title: t('fbpPage.withdrawalModal.createBtn'),
          onClick: () => {
            formRef.current && formRef.current.submit();
          },
          disabled: buttonProps.disabled,
          loading: buttonProps.isLoading,
        },
      ]}
    >
      <Form
        formRef={formRef}
        sellerWarehouseId={sellerWarehouseId}
        onStatusChange={handleStatusChange}
        onSuccess={onSuccess}
        onError={onError}
      />
    </Modal>
  )
}

export default CreateWithdrawalModal;
